/**
 * CRITICAL: AUTH CORE - DO NOT MODIFY
 * 
 * This is the IMMUTABLE auth core that handles all authentication logic.
 * ANY modification to this file MUST go through strict review process.
 * 
 * RULES:
 * 1. NO feature can directly modify this core
 * 2. NO imports from feature-specific modules allowed
 * 3. NO external dependencies except Supabase
 * 4. ALL changes must be backward compatible
 * 5. NEVER add feature-specific logic here
 */

import { createClient, SupabaseClient, User, Session } from '@supabase/supabase-js';

// Sealed interface - cannot be extended
interface AuthState {
  readonly user: User | null;
  readonly session: Session | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
}

// Internal mutable state interface
interface InternalAuthState {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Immutable auth events
type AuthEvent = 
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; user: User; session: Session }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_ERROR'; error: string };

type AuthListener = (state: AuthState) => void;

/**
 * SEALED AUTH CORE CLASS
 * 
 * This class is IMMUTABLE and CANNOT be extended or modified.
 * It provides a single source of truth for authentication state.
 */
class AuthCore {
  private static instance: AuthCore | null = null;
  private supabase: SupabaseClient;
  private state: InternalAuthState;
  private listeners: Set<AuthListener> = new Set();
  private initialized = false;
  private backendLoginInProgress: Set<string> = new Set(); // Track ongoing backend login calls by user ID

  private constructor() {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      throw new Error('CRITICAL: Supabase credentials missing');
    }

    this.supabase = createClient(supabaseUrl, supabaseKey);
    this.state = {
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: true
    };

    // Instance created successfully
  }

  /**
   * Singleton pattern - only one instance allowed
   */
  public static getInstance(): AuthCore {
    if (!AuthCore.instance) {
      AuthCore.instance = new AuthCore();
    }
    return AuthCore.instance;
  }

  /**
   * Reset instance for debugging purposes
   */
  public static resetInstance(): void {
    AuthCore.instance = null;
  }

  /**
   * Initialize auth core - MUST be called once at app start
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Get initial session
      const { data: { session }, error } = await this.supabase.auth.getSession();
      
      if (error) {
        this.updateState({ type: 'AUTH_ERROR', error: error.message });
        return;
      }

      if (session?.user) {
        this.updateState({ type: 'AUTH_SUCCESS', user: session.user, session });
        
        // Call backend login in background - don't block UI rendering
        this.callBackendLogin(session.access_token).catch(error => {
          console.error('❌ Background backend login failed:', error);
        });
      } else {
        this.updateState({ type: 'AUTH_LOGOUT' });
      }

      // Listen for auth changes
      this.supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          this.updateState({ type: 'AUTH_SUCCESS', user: session.user, session });
          
          // Call backend login in background - don't block UI
          this.callBackendLogin(session.access_token).catch(error => {
            console.error('❌ Background backend login failed:', error);
          });
        } else if (event === 'SIGNED_OUT') {
          this.updateState({ type: 'AUTH_LOGOUT' });
        }
      });

      this.initialized = true;
    } catch (error) {
      this.updateState({ 
        type: 'AUTH_ERROR', 
        error: error instanceof Error ? error.message : 'Unknown auth error' 
      });
    }
  }

  /**
   * Get current auth state (read-only)
   */
  public getState(): Readonly<AuthState> {
    return Object.freeze({ ...this.state });
  }

  /**
   * Subscribe to auth state changes
   */
  public subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Login with Google OAuth
   */
  public async loginWithGoogle(): Promise<void> {
    try {
      this.updateState({ type: 'AUTH_LOADING' });
      
      const { error } = await this.supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/loading`
        }
      });

      if (error) {
        this.updateState({ type: 'AUTH_ERROR', error: error.message });
      }
    } catch (error) {
      this.updateState({ 
        type: 'AUTH_ERROR', 
        error: error instanceof Error ? error.message : 'Login failed' 
      });
    }
  }

  /**
   * Login with Facebook OAuth
   */
  public async loginWithFacebook(): Promise<void> {
    try {
      this.updateState({ type: 'AUTH_LOADING' });
      
      const { error } = await this.supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/loading`
        }
      });

      if (error) {
        this.updateState({ type: 'AUTH_ERROR', error: error.message });
      }
    } catch (error) {
      this.updateState({ 
        type: 'AUTH_ERROR', 
        error: error instanceof Error ? error.message : 'Login failed' 
      });
    }
  }

  /**
   * Logout user
   */
  public async logout(): Promise<void> {
    try {
      const { error } = await this.supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
      }
      
      this.updateState({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      console.error('Logout failed:', error);
      // Force logout even if API call fails
      this.updateState({ type: 'AUTH_LOGOUT' });
    }
  }

  /**
   * Force logout for suspended/banned users
   */
  public async forceLogout(reason?: string): Promise<void> {
    console.warn(`🚫 Force logout triggered: ${reason || 'Account access restricted'}`);
    
    try {
      // Clear Supabase session
      await this.supabase.auth.signOut();
    } catch (error) {
      console.error('Error during force logout:', error);
    }
    
    // Clear all auth state
    this.updateState({ type: 'AUTH_LOGOUT' });
    
    // Clear any stored authentication data
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('metapic_token');
    localStorage.removeItem('metapic_user');
    
    // Clear ongoing backend login operations
    this.backendLoginInProgress.clear();
  }

  /**
   * Get access token for backend calls
   */
  public async getAccessToken(): Promise<string | null> {
    try {
      // First try to get current session
      let { data: { session } } = await this.supabase.auth.getSession();

      // If no session or token is expired/expiring soon, try to refresh
      if (!session?.access_token ||
          (session.expires_at && Date.now() / 1000 > session.expires_at - 60)) { // Refresh if expires within 1 minute

        const { data: refreshData, error: refreshError } = await this.supabase.auth.refreshSession();

        if (refreshError) {
          console.error('❌ Session refresh failed:', refreshError);
          return null;
        }

        if (refreshData.session) {
          session = refreshData.session;
        } else {
          return null;
        }
      }


      return session?.access_token || null;
    } catch (error) {
      // [SECURITY] Removed token logging;
      return null;
    }
  }

  /**
   * Get Supabase client instance (for backward compatibility)
   */
  public getSupabaseClient(): SupabaseClient {
    return this.supabase;
  }

  /**
   * Call backend login to create user record and API key
   * This is the KEY method that ensures API keys get created!
   * Prevents race conditions with duplicate calls
   */
  private async callBackendLogin(token: string): Promise<void> {
    try {
      // Get user ID from token to prevent race conditions
      const { data: { user }, error } = await this.supabase.auth.getUser(token);
      if (error || !user) {
        // [SECURITY] Removed token logging;
        return;
      }
      
      const userId = user.id;
      
      // Check if backend login is already in progress for this user
      if (this.backendLoginInProgress.has(userId)) {
        return;
      }
      
      // Mark backend login as in progress
      this.backendLoginInProgress.add(userId);
      
      try {
        const backendUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
        
        const response = await fetch(`${backendUrl}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            provider: 'google'
          })
        });
        
        if (response.ok) {
          // Backend login successful
        } else {
          // Handle different error status codes
          const errorData = await response.json().catch(() => null);
          console.error('❌ Backend login failed:', errorData);
          
          // Handle FORBIDDEN (403) - suspended/banned account
          if (response.status === 403 && errorData?.error?.code === 'FORBIDDEN') {
            console.warn('🚫 Account suspended/banned - forcing logout');
            
            // Force logout from Supabase
            await this.supabase.auth.signOut();
            this.updateState({ type: 'AUTH_LOGOUT' });
            
            // Redirect to account restricted page with details
            if (errorData.error.details) {
              const params = new URLSearchParams();
              params.set('status', errorData.error.details.status || 'suspended');
              if (errorData.error.details.reason) params.set('reason', errorData.error.details.reason);
              if (errorData.error.details.suspendedAt) params.set('suspendedAt', errorData.error.details.suspendedAt);
              if (errorData.error.details.bannedAt) params.set('bannedAt', errorData.error.details.bannedAt);
              if (errorData.error.details.expiresAt) params.set('expiresAt', errorData.error.details.expiresAt);
              
              window.location.href = `/account/restricted?${params.toString()}`;
            } else {
              // Fallback - redirect to restricted page without details
              window.location.href = '/account/restricted?status=suspended';
            }
            return;
          }
          
          // Handle other errors (don't logout for other types of backend errors)
        }
      } finally {
        // Always remove the user from in-progress set
        this.backendLoginInProgress.delete(userId);
      }
    } catch (error) {
      console.error('❌ Error calling backend login:', error);
      // Don't throw error - login should still work even if backend call fails
    }
  }

  /**
   * Private method to update state and notify listeners
   */
  private updateState(event: AuthEvent): void {
    switch (event.type) {
      case 'AUTH_LOADING':
        this.state = {
          ...this.state,
          isLoading: true
        };
        break;
        
      case 'AUTH_SUCCESS':
        this.state = {
          user: event.user,
          session: event.session,
          isAuthenticated: true,
          isLoading: false
        };
        break;
        
      case 'AUTH_LOGOUT':
        this.state = {
          user: null,
          session: null,
          isAuthenticated: false,
          isLoading: false
        };
        break;
        
      case 'AUTH_ERROR':
        this.state = {
          ...this.state,
          isLoading: false
        };
        console.error('Auth error:', event.error);
        break;
    }

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(this.getState());
      } catch (error) {
        console.error('Auth listener error:', error);
      }
    });
  }
}

// // Export singleton instance
export const authCore = AuthCore.getInstance();

// Export class and types
export { AuthCore };
export type { AuthState };

// Export singleton instance - ready for use